/**
 * APPLYING THE DECISION TO A LIVE ROUTER, WITHOUT TOUCHING THE ROUTE TABLE.
 *
 * WHY A ROUTER SUBSCRIPTION AND NOT A COMPONENT. A React component that reads
 * `useLocation` has to be mounted somewhere, and on this surface the only
 * places to mount one are inside each route or inside a new pathless layout
 * route wrapping them all. The second changes the shape of `routes`, which
 * `router/index.test.tsx` guards precisely because every top-level path costs
 * a name out of the slug space — a booking link is printed on a card and that
 * door only closes one way. Analytics is not a good enough reason to reshape
 * the routing contract, so it observes the router instead of living in it.
 *
 * THE CONSENT ANSWER IS READ FRESH ON EVERY NAVIGATION, deliberately. A value
 * captured at mount would let a withdrawal in another tab, or one made after
 * the page loaded, keep collecting until a reload. The store is the source of
 * truth and reading it costs one `localStorage.getItem` per navigation.
 */
import { readConsent, type ConsentChoice } from "./consent";
import { decide } from "./decide";
import * as adapter from "./gtag";
import type { Category } from "./routes";
import { outcomeFor } from "./outcome";
import type { BookingState } from "../features/booking/useBooking";
import type { Outcome, Step } from "./events";

/**
 * THE SEMANTIC EVENT A CATEGORY IMPLIES, IF ANY.
 *
 * Product's taxonomy has one event per meaningful view, and the category is
 * already the meaning — so the producer lives here, beside the page view it
 * accompanies, rather than as an analytics call scattered through the views.
 * Two reasons that matter beyond tidiness: the dedupe that stops a re-render
 * or a query rewrite producing a second event is the same dedupe, and a view
 * that never imports the analytics module cannot accidentally send from a
 * render path.
 *
 * `landing` has no semantic event — Product approved three, and a landing view
 * is not one of them. `booking_outcome` is not here either: an outcome is not
 * a route, and inferring it from one is the thing `outcome.ts` exists to
 * refuse.
 */
const STEP_FOR: Partial<Record<Category, Step>> = {
  booking_service: "service",
  booking_time: "time",
  booking_details: "details",
  booking_review: "review",
};

/**
 * The last category actually reported.
 *
 * A CATEGORY, not a pathname. Two professionals' pages are the same measured
 * page, the booking flow rewrites its query string constantly, and a
 * `replaceState` is not a new page — keying on the category is what makes all
 * three produce one event instead of many. It also means no pathname is ever
 * held in module state.
 */
let lastReported: Category | null = null;
let attached = false;

/** Apply the decision for one location. Returns what it did, for tests. */
export function applyDecision(pathname: string, hostname: string): "sent" | "duplicate" | "refused" {
  const decision = decide({ hostname, pathname, consent: readConsent() });

  if (!decision.collect) {
    // Entering a credential route latches the adapter off for the life of this
    // document. It should be unreachable with the library loaded — there is no
    // in-app navigation into `/a/**`, which
    // `tests/unit/book-analytics-isolation.test.js` pins — and this is the belt
    // to that brace, not a substitute for it.
    if (decision.refusals.includes("credential-route")) adapter.shutdown();
    return "refused";
  }

  if (adapter.phase() === "idle") {
    adapter.initialise(decision.measurementId, decision.category);
  }
  if (lastReported === decision.category) return "duplicate";
  if (!adapter.sendPageView(decision.category)) return "refused";
  lastReported = decision.category;

  // The semantic event rides with the page view and is deduped by the same
  // key, so a re-render, a StrictMode double effect, a query rewrite, a
  // `replaceState` and a retry render all produce one of each or neither.
  if (decision.category === "professional_profile") {
    adapter.send("booking_profile_viewed");
  } else {
    const step = STEP_FOR[decision.category];
    if (step) adapter.send("booking_step_viewed", { step });
  }
  return "sent";
}

/**
 * Observe a router for the life of the document.
 *
 * Idempotent. `attached` is a module latch rather than a check on the router,
 * because a second subscription would double every page view and the symptom
 * would be a doubled count in a report nobody looks at for a week.
 */
export function attachToRouter(router: {
  subscribe: (listener: (state: { location: { pathname: string } }) => void) => () => void;
  state: { location: { pathname: string } };
}): void {
  if (attached) return;
  attached = true;
  const hostname = () => window.location.hostname;
  applyDecision(router.state.location.pathname, hostname());
  router.subscribe((state) => applyDecision(state.location.pathname, hostname()));
}

/**
 * A PREFERENCE CHANGED ON THIS PAGE. NO RELOAD.
 *
 * Product's contract: after an affirmative choice the adapter begins operating
 * on the page the visitor is already looking at. A reload would be the easy
 * implementation and the wrong one — it is destructive on this surface, where
 * the booking flow's selection lives in the query string and a hold may be
 * live. Somebody answering a cookie question must not lose a slot for it.
 *
 * `granted` from `idle` runs the full decision, which loads and configures.
 * `granted` from `suspended` re-grants through the consent command only — no
 * second `config`, because the session already exists.
 * `denied` suspends and forgets what was last reported, so a later re-grant
 * reports the current page rather than treating it as a duplicate.
 *
 * A credential route is never affected: `stopped` refuses both directions, and
 * `decide` would refuse anyway.
 */
export function applyConsentChange(
  choice: ConsentChoice,
  pathname: string,
  hostname: string
): "sent" | "suspended" | "resumed" | "refused" {
  if (choice === "denied") {
    adapter.suspend();
    lastReported = null;
    return "suspended";
  }
  if (adapter.phase() === "suspended") {
    if (!adapter.resume()) return "refused";
    const outcome = applyDecision(pathname, hostname);
    return outcome === "sent" ? "resumed" : "refused";
  }
  return applyDecision(pathname, hostname) === "sent" ? "sent" : "refused";
}

/**
 * The outcome the SERVER produced, reported once.
 *
 * Called from the view that owns the booking state, because that is the only
 * place the authoritative result exists. `outcomeFor` decides whether the
 * state is one of Product's four at all; an unmapped state sends nothing.
 *
 * Deduped on the VALUE, not on a render count: `useBooking` can re-enter
 * `uncertain` while a recovery check runs, and the outcome screen re-renders
 * for reasons that have nothing to do with the appointment changing.
 *
 * TWO EVENTS FOR ONE BOOKING IS CORRECT, and an earlier version of this comment
 * said the opposite. `uncertain` resolving to `done` through `applyOutcome` is
 * the recovery path the whole state exists for, so an `indeterminate` view
 * followed by a `confirmed` one emits both — and it should. These are VIEW
 * events; the visitor saw both screens. Suppressing the resolved one to make a
 * uniqueness claim true would report less than happened. The consequence for a
 * report is that `indeterminate` is inflated relative to the other three, which
 * is documented rather than engineered away.
 */
let lastOutcome: Outcome | null = null;

export function reportOutcome(state: BookingState): "sent" | "duplicate" | "refused" {
  const outcome = outcomeFor(state);
  if (outcome === null) return "refused";
  if (lastOutcome === outcome) return "duplicate";
  if (!adapter.send("booking_outcome_viewed", { outcome })) return "refused";
  lastOutcome = outcome;
  return "sent";
}

/** Test-only reset. */
export function __resetForTests(): void {
  lastReported = null;
  lastOutcome = null;
  attached = false;
}
